import { ErrorHandler, Injectable, NgZone, inject } from '@angular/core';
import { ConsoleErrorEntry, HttpFailureEntry, MAX_BUFFERED } from '../bug-report.model';

/**
 * The last few things that went wrong, kept in memory so a bug report can carry them.
 *
 * Nothing in this app recorded either of these before. A report used to say "the page was broken"
 * and a triager had no way to see the error that caused it, which is the single most useful thing
 * a report can carry.
 *
 * Two ring buffers, bounded at MAX_BUFFERED. Bounded because this runs for the whole session: an
 * unbounded list on a page that throws in a render loop is a memory leak, and the oldest entries
 * are the least likely to be the one the user is reporting.
 *
 * **Never put a request body, a response body or a header in here.** The Authorization header
 * carries a bearer token and an error message can quote a customer record. Failures are recorded
 * as method, URL, status and message, which is enough to find the call in a log and nothing more.
 */
@Injectable({ providedIn: 'root' })
export class ErrorBufferService {
  private readonly consoleErrors: ConsoleErrorEntry[] = [];
  private readonly httpFailures: HttpFailureEntry[] = [];

  recordError(error: unknown): void {
    const { message, stack } = describe(error);
    push(this.consoleErrors, {
      at: new Date().toISOString(),
      message: trim(message, 2000),
      ...(stack ? { stack: trim(stack, 4000) } : {}),
    });
  }

  recordHttpFailure(entry: HttpFailureEntry): void {
    push(this.httpFailures, {
      ...entry,
      url: trim(entry.url, 512),
      method: trim(entry.method, 10),
      ...(entry.message ? { message: trim(entry.message, 500) } : {}),
    });
  }

  /** Copies, so a report cannot be mutated by anything that happens after it is assembled. */
  snapshot(): { consoleErrors: ConsoleErrorEntry[]; httpFailures: HttpFailureEntry[] } {
    return {
      consoleErrors: [...this.consoleErrors],
      httpFailures: [...this.httpFailures],
    };
  }

  clear(): void {
    this.consoleErrors.length = 0;
    this.httpFailures.length = 0;
  }
}

function push<T>(buffer: T[], entry: T): void {
  buffer.push(entry);
  // Oldest first, so the report reads in the order things happened.
  while (buffer.length > MAX_BUFFERED) buffer.shift();
}

function trim(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function describe(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return { message: error.message || error.name, ...(error.stack ? { stack: error.stack } : {}) };
  }
  if (typeof error === 'string') return { message: error };
  try {
    return { message: JSON.stringify(error) ?? String(error) };
  } catch {
    return { message: String(error) };
  }
}

/**
 * The app's first custom ErrorHandler.
 *
 * It records and then delegates to Angular's default, so console output and existing behaviour are
 * unchanged. Swallowing the error here would make this feature the reason a developer could no
 * longer see a stack trace, which would be a poor trade for a diagnostic aid.
 */
@Injectable()
export class BugReportErrorHandler implements ErrorHandler {
  private readonly buffer = inject(ErrorBufferService);
  private readonly zone = inject(NgZone);
  private readonly delegate = new ErrorHandler();

  constructor() {
    // Outside Angular: an unhandled rejection is not a template event and running the listener
    // inside the zone would schedule a change detection pass on every one.
    this.zone.runOutsideAngular(() => {
      window.addEventListener('unhandledrejection', (event) => {
        this.buffer.recordError(event.reason);
      });
    });
  }

  handleError(error: unknown): void {
    this.buffer.recordError(error);
    this.delegate.handleError(error);
  }
}
